import { useQuery } from "@tanstack/react-query";
import { validateField } from "./../../Utils/helperFunction";
import React, { useContext, createContext, useState } from "react";
import { Selfie } from "./../../api";

const authContext = createContext(undefined);

export const useAuth = () => useContext(authContext);

function useProvideAuth() {
  const [globalAccountData, setGlobalAccountData] = useState(null);
  const [userList, setUserList] = useState([]);
  const [signupOtpToken, setSignupOtpToken] = useState("");
  const [signupOtp, setSignupOtp] = useState("");
  const [panError, setPanError] = useState("");

  const [confirmPass, setConfirmPass] = useState("");
  const [password, setPassword] = useState("");
  const [userName, setUserName] = useState("");
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    mobileNumber: "",
    pan: "",
    dob: "",
    postcode: "",
    address: "",
    city: "",
    state: "",
  });
  // Make sure these states are defined in your component or context
  const [isPdf, setIsPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stateVariable, setStateVariable] = useState(1);
  const [pincode, setpincode] = useState("");
  const [loading, setLoading] = useState(false);

  // to Remove the leading and trailing spaces from the input fields
  const normalizeFormData = () => {
    const trimmedData = { ...formData };
    Object.keys(trimmedData).forEach((key) => {
      // Check if the value is a string before trimming
      if (typeof trimmedData[key] === "string") {
        trimmedData[key] = trimmedData[key].trim();
      }
    });
    setFormData(trimmedData);
  };

  const handleFieldChange = (e, fieldName) => {
    let value =
      fieldName === "pan" ? e.target.value.toUpperCase() : e.target.value;
    const name = fieldName || e.target.name;

    if (name === "pan") {
      value = value.toUpperCase();
    }

    // For all other fields - capitalize first letter of each word
    else if (typeof value === "string") {
      value = value
        ?.split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word?.slice(1).toLowerCase()
        )
        .join(" ");
    }

    if (name === "mobileNumber") {
      if (value.length > 10) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          mobileNumber: "Mobile number must be 10 digits",
        }));
        return; // Exit early, don't update `formData`
      } else {
        // Clear the error if the length is exactly 10
        setErrors((prevErrors) => {
          const { mobileNumber, ...otherErrors } = prevErrors;
          return otherErrors;
        });
      }
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    // setFormData((prevData) => ({
    //   ...prevData,
    //   [name]: fieldName === "pan" ? value : value,
    // }));

    if (errors[name]) {
      const error = validateField(name, value);
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: error,
      }));
    }
  };

  const handleFieldBlur = (e, fieldName) => {
    const value =
      fieldName === "pan" ? e.target.value.toUpperCase() : e.target.value;
    const name = fieldName || e.target.name;
    if (name === "postcode") {
      setpincode(value);
    }

    const error = validateField(name, value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  // validate again before submitting the form
  const validateForm = (formFields) => {
    const newErrors = {};
    let isValid = true;

    Object.keys(formFields).forEach((key) => {
      const error = validateField(key, formFields[key]);
      if (error) {
        newErrors[key] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const validateFormPage1 = () =>
    validateForm({
      firstName: formData.firstName,
      lastName: formData.lastName,
      mobileNumber: formData.mobileNumber,
      pan: formData.pan,
      dob: formData.dob,
    });

  // In your validateFormPage2 function (in useAuth.js), modify it to preserve existing API errors
  const validateFormPage2 = () => {
    // Create a copy of the current errors to preserve API errors
    const currentErrors = { ...errors };

    // Run validation on required fields
    const formFieldsToValidate = {
      address: formData.address,
      city: formData.city,
      state: formData.state,
      postcode: formData.postcode,
    };

    const newErrors = {};
    let isValid = true;

    Object.keys(formFieldsToValidate).forEach((key) => {
      // Skip postcode validation if there's already an API error for it
      if (key === "postcode" && currentErrors.postcode) {
        newErrors.postcode = currentErrors.postcode;
        isValid = false;
        return;
      }

      const error = validateField(key, formFieldsToValidate[key]);
      if (error) {
        newErrors[key] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const checkImageCorruption = (file) => {
    return new Promise((resolve, reject) => {
      // Only process image files
      if (!file.type.startsWith("image/")) {
        resolve(file);
        return;
      }

      const img = new Image();
      const imageUrl = URL.createObjectURL(file);

      // Set up timeout to handle cases where onload/onerror might not fire
      const timeoutId = setTimeout(() => {
        URL.revokeObjectURL(imageUrl);
        reject(
          new Error("Image loading timed out. The file may be corrupted.")
        );
      }, 10000); // 10 second timeout

      img.onload = () => {
        clearTimeout(timeoutId);
        URL.revokeObjectURL(imageUrl);

        // Additional checks for image validity
        if (img.width === 0 || img.height === 0) {
          reject(
            new Error(
              "Image has invalid dimensions. The file may be corrupted."
            )
          );
          return;
        }

        resolve(file);
      };

      img.onerror = () => {
        clearTimeout(timeoutId);
        URL.revokeObjectURL(imageUrl);
        reject(
          new Error(
            "The file appears to be protected or corrupted. Please upload a valid file.."
          )
        );
      };

      img.src = imageUrl;
    });
  };

  // Then in your handleImageUpload function:
  const handleImageUpload = (event) => {
    const file = event.target.files[0];

    // Clear previous errors and states
    setErrorMessage("");

    // Basic validations (size, type, etc.)
    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage("Image size exceeds 5 MB limit");
      return;
    }

    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/pdf",
    ];
    if (!validTypes.includes(file.type)) {
      setErrorMessage(
        "Invalid file type. Only JPEG, JPG, PNG, and PDF are allowed"
      );
      return;
    }

    // If it's an image, check for corruption
    if (file.type.startsWith("image/")) {
      checkImageCorruption(file)
        .then((validFile) => {
          // Image is valid, proceed
          const imageUrl = URL.createObjectURL(validFile);
          setImageFile(validFile);
          setImagePreview(imageUrl);
          if (stateVariable === 3) {
            setStateVariable(stateVariable + 1);
          }
        })
        .catch((error) => {
          setErrorMessage(error.message);
        });
    }
    // For PDFs, just proceed with basic validation
    else if (file.type === "application/pdf") {
      const pdfUrl = URL.createObjectURL(file);
      setImageFile(file);
      setImagePreview(pdfUrl);
      setPdfUrl(pdfUrl);
      setIsPdf(true);

      if (stateVariable === 3) {
        setStateVariable(stateVariable + 1);
      }
    }
  };

  const useSelfie = () => {
    const { isLoading, isError, data, error } = useQuery({
      queryKey: ["beneficiarySelfieDetails"],
      queryFn: Selfie,
      refetchOnWindowFocus: true,
      staleTime: Infinity,

      // onSuccess: (beneficiaryDataList) => {
      // setBeneficiaryData(beneficiaryDataList?.data?.beneficiaries);
      // },
    });
    return { isLoading, isError, data, error };
  };

  return {
    globalAccountData,
    setGlobalAccountData,
    // confirmPass State
    confirmPass,
    panError,
    setPanError,
    setConfirmPass,
    userList,
    setUserList,
    signupOtpToken,
    setSignupOtpToken,
    signupOtp,
    setSignupOtp,
    password,
    setPassword,
    userName,
    setUserName,
    formData,
    setFormData,
    errors,
    setErrors,
    imageFile,
    setImageFile,
    errorMessage,
    setErrorMessage,
    isSubmitting,
    setIsSubmitting,
    // Reusable Functions
    normalizeFormData,
    handleFieldChange,

    validateFormPage2,
    validateFormPage1,
    handleFieldBlur,
    stateVariable,
    handleImageUpload,
    setStateVariable,
    imagePreview,
    setImagePreview,
    useSelfie,
    pincode,
    setpincode,
    isPdf,
    setIsPdf,
    pdfUrl,
    setPdfUrl,
    loading,
    setLoading,
  };
}

export function ProvideAuth({ children }) {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}
